import React from "react";
import { formatINR } from "./ewayBillUtils";
import { QrCodeSvg } from "../../utils/QrCodeSvg";
import { getStateLabel } from "../../utils/GstStates";
import { es, SectionTitle, Field } from "./EwayBillPrintStyles";

const money = (v) => formatINR(v ?? 0);

// Party address block (seller / buyer / ship-to) from an e-Invoice party bag.
function PartyBlock({ heading, party }) {
    const p = party || {};
    return (
        <div style={es.addrCol}>
            <div style={es.addrHead}>{heading}</div>
            <div style={es.addrLine}><strong>{p.LglNm || "-"}</strong></div>
            {p.TrdNm && p.TrdNm !== p.LglNm && <div style={es.addrLine}>{p.TrdNm}</div>}
            <div style={es.addrLine}>GSTIN: {p.Gstin || "-"}</div>
            <div style={es.addrLine}>{[p.Addr1, p.Addr2].filter(Boolean).join(", ") || "-"}</div>
            <div style={es.addrLine}>
                {[p.Loc, p.Pin].filter(Boolean).join(" - ")}
                {p.Stcd ? `, ${getStateLabel(Number(p.Stcd))}` : ""}
            </div>
            {p.Ph && <div style={es.addrLine}>Ph: {p.Ph}</div>}
        </div>
    );
}

/**
 * NIC-format printable e-Invoice.
 *
 * The QR is the portal's SignedQRCode verbatim — a JWS of roughly 1-1.4 KB. It is rendered
 * at error-correction level "L" and a larger size than the e-Way bill QR because at level
 * "M" a payload that size can overflow the symbol's capacity, and a 177-module code needs
 * the extra pixels to stay scannable on paper.
 */
export function EInvoicePrintSheet({ eInvoice }) {
    const p = eInvoice.requestPayload || {};
    const cancelled = String(eInvoice.status || "").toUpperCase() === "CANCELLED";
    const items = p.ItemList || [];
    const val = p.ValDtls || {};
    const doc = p.DocDtls || {};

    const totals = [
        ["Tot. Tax'ble Amt", val.AssVal],
        ["CGST Amt", val.CgstVal],
        ["SGST Amt", val.SgstVal],
        ["IGST Amt", val.IgstVal],
        ["CESS Amt", val.CesVal],
        ["Discount", val.Discount],
        ["Round Off", val.RndOffAmt],
        ["Total Inv.Amt", val.TotInvVal],
    ];

    return (
        <div className="print-receipt bg-white" style={es.container}>
            {cancelled && <div style={es.watermark}>CANCELLED</div>}

            {/* Header: title + IRN acknowledgement, with the signed QR on the right */}
            <div style={es.headerRow}>
                <div>
                    {/* When NIC issued the e-Way bill alongside the IRN this one sheet is both
                        documents. Saying so at the top matters at a checkpoint: an officer
                        looking for an e-Way bill should not have to read to section 5 to find
                        out that this page is it. */}
                    <div style={es.title}>
                        e-Invoice
                        {eInvoice.ewbNo && (
                            <span style={{ fontSize: "15px", fontWeight: "normal" }}> cum e-Way Bill</span>
                        )}
                    </div>
                    <div style={{ fontSize: "10px", color: "#555" }}>
                        Generated under Rule 48(4) of the CGST Rules
                    </div>
                    {eInvoice.ewbNo && (
                        <div style={{ fontSize: "11px", marginTop: "5px" }}>
                            <strong>e-Way Bill {eInvoice.ewbNo}</strong>
                            {eInvoice.ewbValidTill && (
                                <span style={{ color: "#555" }}> · valid until {eInvoice.ewbValidTill}</span>
                            )}
                        </div>
                    )}
                    {cancelled && <span style={es.cancelBadge}>CANCELLED</span>}
                </div>
                {/* Sized from the physics, not aesthetics: the signed QR is ~950 bytes, which
                    even at error-correction level L needs a 105x105 module symbol. At the
                    150px the e-Way bill QR uses, that is 0.35mm per module — under the ~0.4mm
                    scanners need, so it prints as an unreadable smudge. 200px gives ~0.48mm. */}
                <div style={{ ...es.qrBox, width: "200px", height: "200px" }}>
                    {eInvoice.signedQRCode ? (
                        <QrCodeSvg
                            value={eInvoice.signedQRCode}
                            size={196}
                            level="L"
                            label="Signed e-Invoice QR code"
                        />
                    ) : (
                        <div style={es.qrInner}>
                            <span style={es.qrHint}>No signed QR</span>
                        </div>
                    )}
                </div>
            </div>

            <SectionTitle>1. e-Invoice Details</SectionTitle>
            <div style={es.grid4}>
                <Field label="Ack No." value={eInvoice.ackNo} strong />
                <Field label="Ack Date" value={eInvoice.ackDt} />
                <Field label="Document No." value={doc.No} strong />
                <Field label="Document Date" value={doc.Dt} />
            </div>
            <div style={{ ...es.grid2, gridTemplateColumns: "1fr", borderTop: "none" }}>
                <div style={{ ...es.field, borderRight: "none" }}>
                    <div style={es.fieldLabel}>IRN (Invoice Reference Number)</div>
                    <div style={{ ...es.fieldValue, fontFamily: "monospace", fontSize: "10px" }}>
                        {eInvoice.irn || "-"}
                    </div>
                </div>
            </div>

            <SectionTitle>2. Transaction Details</SectionTitle>
            <div style={es.grid4}>
                <Field label="Supply Type" value={(p.TranDtls || {}).SupTyp} />
                <Field label="Document Type" value={doc.Typ === "INV" ? "Invoice" : doc.Typ} />
                <Field label="Place of Supply"
                    value={(p.BuyerDtls || {}).Pos ? getStateLabel(Number(p.BuyerDtls.Pos)) : "-"} />
                <Field label="Reverse Charge" value={(p.TranDtls || {}).RegRev === "Y" ? "Yes" : "No"} />
            </div>

            <SectionTitle>3. Party Details</SectionTitle>
            <div style={{ ...es.grid2, gridTemplateColumns: p.ShipDtls ? "1fr 1fr 1fr" : "1fr 1fr" }}>
                <PartyBlock heading="Supplier" party={p.SellerDtls} />
                <PartyBlock heading="Recipient" party={p.BuyerDtls} />
                {p.ShipDtls && <PartyBlock heading="Ship To" party={p.ShipDtls} />}
            </div>

            <SectionTitle>4. Goods / Services Details</SectionTitle>
            <table style={es.table}>
                <thead>
                    <tr style={es.theadRow}>
                        <th style={es.th}>Sl</th>
                        <th style={es.th}>Description</th>
                        <th style={es.th}>HSN</th>
                        <th style={es.thNum}>Qty</th>
                        <th style={es.th}>Unit</th>
                        <th style={es.thNum}>Rate</th>
                        <th style={es.thNum}>Discount</th>
                        <th style={es.thNum}>Taxable</th>
                        <th style={es.thNum}>GST %</th>
                        <th style={es.thNum}>Item Total</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((it, idx) => (
                        <tr key={idx}>
                            <td style={es.td}>{it.SlNo}</td>
                            <td style={{ ...es.td, textAlign: "left" }}>
                                {it.PrdDesc}{it.IsServc === "Y" ? " (Service)" : ""}
                            </td>
                            <td style={es.td}>{it.HsnCd}</td>
                            <td style={es.tdNum}>{it.Qty}</td>
                            <td style={es.td}>{it.Unit}</td>
                            <td style={es.tdNum}>{money(it.UnitPrice)}</td>
                            <td style={es.tdNum}>{money(it.Discount)}</td>
                            <td style={es.tdNum}>{money(it.AssAmt)}</td>
                            <td style={es.tdNum}>{Number(it.GstRt) || 0}</td>
                            <td style={es.tdNum}>{money(it.TotItemVal)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={es.totalsStrip}>
                {totals.map(([label, value]) => (
                    <div key={label} style={es.totalBox}>
                        <div style={es.totalLabel}>{label}</div>
                        <div style={es.totalVal}>{money(value)}</div>
                    </div>
                ))}
            </div>

            {eInvoice.ewbNo && (
                <>
                    <SectionTitle>5. e-Way Bill Details</SectionTitle>
                    <div style={es.grid4}>
                        <Field label="e-Way Bill No." value={eInvoice.ewbNo} strong />
                        <Field label="Generated On" value={eInvoice.ewbDt} />
                        <Field label="Valid Until" value={eInvoice.ewbValidTill} />
                        <Field label="Vehicle No." value={eInvoice.vehicleNo} />
                    </div>
                </>
            )}

            {cancelled && (
                <>
                    <SectionTitle>Cancellation</SectionTitle>
                    <div style={es.grid4}>
                        <Field label="Cancelled On" value={eInvoice.cancelDate} />
                        <Field label="Reason Code" value={eInvoice.cancelReasonCode} />
                        <Field label="Remarks" value={eInvoice.cancelRemarks} />
                        <Field label="Status" value="CANCELLED" strong />
                    </div>
                </>
            )}

            <div style={es.brandFooter}>
                <div>Digitally signed by the National Informatics Centre (NIC) — verify at einvoice1.gst.gov.in</div>
                <div style={{ marginTop: "3px", fontSize: "10px", color: "#666" }}>
                    Powered by Revcon Technologies • revcontech.com
                </div>
            </div>
        </div>
    );
}
